import {useDeferredValue, useEffect, useMemo, useRef} from 'react'

import type {CopilotChatManager} from '@github-ui/copilot-chat/utils/copilot-chat-manager'
import type {CopilotChatReference, ImageReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import type {DotcomFileAttachment} from '@github-ui/copilot-chat/utils/uploadable-file-attachment'
import {ImageReferenceToken} from '../components/ImageReferenceToken'
import {extractImageUrls} from '../utils/markdown-images'
import {MAX_TASK_IMAGES} from './use-markdown-image-upload'

/**
 * Legacy attachment path pattern: /<org>/<repo>/assets/<id>/<guid>
 * The guid should be alphanumeric/hyphens without a file extension.
 */
const LEGACY_ASSET_PATH_PATTERN = /^\/[\w.-]+\/[\w.-]+\/assets\/\d+\/[^.]+$/

/**
 * Creates a minimal DotcomFileAttachment for URL-only image references.
 * Used when we detect an image URL in markdown that's already uploaded to GitHub,
 * so no actual upload is needed - we just wrap the existing URL.
 */
function createUrlAttachment(url: string, name: string): DotcomFileAttachment {
  const dummyFile = new File([], name, {type: 'image/*'})
  return {
    key: url,
    file: dummyFile,
    previewUrl: url,
    url: async () => url,
    prefetch: async () => {},
    isLoaded: true,
    hasError: false,
    threadID: null,
    getDimensions: async () => ({width: undefined, height: undefined}),
  }
}

/**
 * Check if a URL is a GitHub asset image URL that sweagentd can resolve.
 * Matches both user attachments and legacy attachments.
 *
 * Supported patterns:
 * - User attachments: ${origin}/user-attachments/assets/...
 * - Legacy attachments: ${origin}/<org>/<repo>/assets/<id>/<guid>
 */
function isGitHubAssetUrl(urlString: string): boolean {
  let url: URL
  try {
    url = new URL(urlString, window.location.origin)
  } catch {
    return false
  }

  // URL must be same-origin
  if (url.origin !== window.location.origin) {
    return false
  }

  // User attachments: /user-attachments/assets/...
  if (url.pathname.startsWith('/user-attachments/assets/')) {
    return true
  }

  // Legacy attachments: /<org>/<repo>/assets/<id>/<guid>
  return LEGACY_ASSET_PATH_PATTERN.test(url.pathname)
}

export interface UseImageReferenceSyncOptions {
  /** Current textarea text content */
  text: string
  /** Chat manager to add/remove references */
  manager: CopilotChatManager
  /** Current references from chat state (used to find references to remove) */
  currentReferences: CopilotChatReference[]
  /** Whether sync is enabled (default: true) */
  enabled?: boolean
}

export interface UseImageReferenceSyncReturn {
  /** Whether the number of images in the text has reached MAX_TASK_IMAGES. */
  isAtImageLimit: boolean
}

/**
 * Extract image URLs that are already tracked as references in the current
 * reference list.
 */
function getExistingImageUrls(references: CopilotChatReference[]): Set<string> {
  const urls = new Set<string>()
  for (const ref of references) {
    if (ref.type === 'image') {
      const imageUrl = ref.imageUrl
      if (imageUrl) urls.add(imageUrl)
    }
  }
  return urls
}

/**
 * Syncs image references in textarea text with CopilotChatManager references.
 *
 * When a GitHub asset image URL is detected in markdown `![alt](url)` or HTML `<img src="url">`:
 * - Creates an ImageReference and adds it via manager.addReference()
 * - Removes the reference when the image is deleted from text
 *
 * Also tracks whether the image count has reached MAX_TASK_IMAGES so callers
 * can disable further image attachment UI without extra parsing.
 */
export function useImageReferenceSync({
  text,
  manager,
  currentReferences,
  enabled = true,
}: UseImageReferenceSyncOptions): UseImageReferenceSyncReturn {
  const deferredText = useDeferredValue(text)
  const previousUrlsRef = useRef<Set<string>>(getExistingImageUrls(currentReferences))

  const githubImages = useMemo(() => {
    if (!enabled) return []
    return extractImageUrls(deferredText).filter(({url}) => isGitHubAssetUrl(url))
  }, [deferredText, enabled])

  const isAtImageLimit = githubImages.length >= MAX_TASK_IMAGES

  useEffect(() => {
    if (!enabled) return

    const currentUrls = new Set<string>()

    for (const {url, alt} of githubImages) {
      currentUrls.add(url)

      // Add new references for newly detected images
      if (!previousUrlsRef.current.has(url)) {
        const name = alt || 'Image'
        const imageRef: ImageReference = {
          type: 'image',
          id: `image:${url}`,
          name,
          imageUrl: url,
          attachment: createUrlAttachment(url, name),
          TokenComponent: ImageReferenceToken,
        }
        manager.addReference(imageRef, 'markdown-detection')
      }
    }

    // Remove references for images no longer in text
    for (const url of previousUrlsRef.current) {
      if (!currentUrls.has(url)) {
        const refToRemove = currentReferences.find(
          (ref): ref is ImageReference => ref.type === 'image' && ref.id === `image:${url}`,
        )
        if (refToRemove) {
          manager.removeReference(refToRemove)
        }
      }
    }

    previousUrlsRef.current = currentUrls
  }, [githubImages, manager, currentReferences, enabled])

  return {isAtImageLimit}
}
